// Overview.js / Profile.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { 
  Avatar, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  CircularProgress,
  Accordion, AccordionSummary, AccordionDetails,
  Switch, // Added for toggle
} from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';  // Fixed import
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { Editor } from 'primereact/editor';
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useTheme } from '@mui/material/styles';
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { collection, query, where, getDocs } from 'firebase/firestore';
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDInput from "components/MDInput";
import Footer from "examples/Footer";
import { db, onSnapshot, auth, orderBy } from "../../firebaseConfig";
// In index.js (Profile/Overview), update this line:
import { MEMBERSHIP_PLANS } from '../authentication/sign-up/SignUpPage';
import { Checkbox, FormControlLabel, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info'; // Add this to your imports at the top
import Tooltip from '@mui/material/Tooltip'; // Add this too
import { WarningIcon } from '@mui/icons-material'; // For status indicators
import Warning from '@mui/icons-material/Warning'; // Correct import for WarningIcon
import { CheckCircle, Cancel } from '@mui/icons-material'; // Add for status icons
import { LinearProgress, Box } from '@mui/material'; // Added for progress bars
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const CoachLogsCard = ({ logs, loading, theme }) => {
  if (loading) {
    return (
      <Card
        style={{
          minHeight: '500px',
          backgroundColor: 'inherit',
          color: 'inherit',
        }}
        sx={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        }}
      >
        <CardHeader
          title={
            <MDTypography variant="h6" sx={{ fontWeight: 500 }}>
              Coach Logs
            </MDTypography>
          }
          sx={{ pb: 1 }}
        />
        <CardContent>
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress size={24} />
          </MDBox>
        </CardContent>
      </Card>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <Card
        style={{
          minHeight: '500px',
          backgroundColor: 'inherit',
          color: 'inherit',
        }}
        sx={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        }}
      >
        <CardHeader
          title={
            <MDTypography variant="h6" sx={{ fontWeight: 500,}}>
              Coach Logs
            </MDTypography>
          }
          sx={{ pb: 1 }}
        />
        <CardContent>
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="200px"
            gap={1}
          >
            <MDTypography variant="body2">
              No coach logs available.
            </MDTypography>
            <MDTypography sx={{ fontSize: '1.5rem', opacity: 0.5 }}>
              📝
            </MDTypography>
          </MDBox>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      style={{
        minHeight: '500px',
        backgroundColor: 'inherit',
        color: 'inherit',
      }}
      sx={{
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardHeader
        title={
          <MDTypography variant="h6" sx={{ fontWeight: 500}}>
            Coach Logs
          </MDTypography>
        }
        sx={{ pb: 1 }}
      />
      <CardContent>
        <MDBox display="flex" flexDirection="column" gap={1}>
          {logs.map((log) => (
            <Accordion
              key={log.id}
              sx={{
                bgcolor: 'transparent',
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '6px',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{  }} />}
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' },
                }}
              >
                <MDTypography variant="subtitle2" sx={{  }}>
                  {new Date(log.createdAt).toLocaleString()} by Coach
                  {log.updatedAt && (
                    <MDTypography variant="caption" color="text.secondary" ml={1}>
                      (Edited {new Date(log.updatedAt).toLocaleString()})
                    </MDTypography>
                  )}
                </MDTypography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: theme.palette.background.default }}>
                <MDTypography variant="body2" sx={{  }}>
                  {log.note}
                </MDTypography>
              </AccordionDetails>
            </Accordion>
          ))}
        </MDBox>
      </CardContent>
    </Card>
  );
};

// Define CoachSetTargetsCard at the top level
const CoachSetTargetsCard = ({ goals, metrics, loading, theme }) => {
  if (!goals) {
    return (
      <Card
        style={{
          minHeight: '350px',
          backgroundColor: 'inherit',
          color: 'inherit',
        }}
        sx={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        }}
      >
        <CardHeader
          title={
            <MDTypography
              variant="h6"
              sx={{ fontWeight: 500 }}
            >
              Coach Set Targets
            </MDTypography>
          }
          sx={{ pb: 1 }}
        />
        <CardContent>
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="200px"
            gap={1}
          >
            <MDTypography variant="body2">
              No targets set by your coach.
            </MDTypography>
            <MDTypography
              sx={{
                fontSize: '1.5rem',
                opacity: 0.5,
              }}
            >
              📋
            </MDTypography>
          </MDBox>
        </CardContent>
      </Card>
    );
  }

  const isOnTarget = (metric, goal) => {
    if (metrics[metric] === null) return false;
    if (goal === undefined || goal === null) return true; // No goal set, assume on target
    switch (metric) {
      case 'winRate':
        return metrics.winRate >= goal;
      case 'maxDrawdown':
        return metrics.maxDrawdown <= goal;
      case 'tradesPerMonth':
        return metrics.tradesPerMonth >= goal;
      case 'stopLoss':
        return metrics.stopLoss <= goal;
      case 'maxTradesPerMonth':
        return metrics.tradesPerMonth <= goal;
      case 'riskRewardRatio':
        return metrics.riskRewardRatio >= goal;
      case 'highConfidenceWinRate':
        return metrics.highConfidenceWinRate >= goal;
      case 'tradeScore':
        return metrics.tradeScore >= goal;
      case 'uniqueSymbols':
        return metrics.uniqueSymbols >= goal;
      case 'maxUniqueSymbols':
        return metrics.uniqueSymbols <= goal;
      default:
        return false;
    }
  };

  // Check if any goals are set
  const hasValidGoals =
    goals.winRate !== undefined && goals.winRate !== null ||
    goals.maxDrawdown !== undefined && goals.maxDrawdown !== null ||
    goals.minTradesPerMonth !== undefined && goals.minTradesPerMonth !== null ||
    goals.maxTradesPerMonth !== undefined && goals.maxTradesPerMonth !== null ||
    goals.stopLoss !== undefined && goals.stopLoss !== null ||
    goals.minRiskRewardRatio !== undefined && goals.minRiskRewardRatio !== null ||
    goals.highConfidenceWinRate !== undefined && goals.highConfidenceWinRate !== null ||
    goals.minTradeScore !== undefined && goals.minTradeScore !== null ||
    goals.minUniqueSymbols !== undefined && goals.minUniqueSymbols !== null ||
    goals.maxUniqueSymbols !== undefined && goals.maxUniqueSymbols !== null;

  if (!hasValidGoals) {
    return (
      <Card
        style={{
          minHeight: '350px',
          backgroundColor: 'inherit',
          color: 'inherit',
        }}
        sx={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        }}
      >
        <CardHeader
          title={
            <MDTypography
              variant="h6"
              sx={{ fontWeight: 500 }}
            >
              Coach Set Targets
            </MDTypography>
          }
          sx={{ pb: 1 }}
        />
        <CardContent>
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="200px"
            gap={1}
          >
            <MDTypography variant="body2">
              No targets set by your coach.
            </MDTypography>
            <MDTypography
              sx={{
                fontSize: '1.5rem',
                opacity: 0.5,
              }}
            >
              📋
            </MDTypography>
          </MDBox>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      style={{
        minHeight: '350px',
        backgroundColor: 'inherit',
        color: 'inherit',
      }}
      sx={{
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardHeader
        title={
          <MDTypography
            variant="h6"
            sx={{ fontWeight: 500 }}
          >
            Team Coach Set Targets
          </MDTypography>
        }
        sx={{ pb: 1 }}
      />
      <CardContent>
        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress size={24} />
          </MDBox>
        ) : (
          <MDBox display="flex" flexDirection="column" gap={1.5}>
            {/* Win Rate */}
            {goals.winRate !== undefined && goals.winRate !== null && (
              <MDBox
                display="flex"
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: '6px',
                  transition: 'transform 0.2s ease',
                  animation: 'fadeIn 0.3s ease-in-out forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <MDTypography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: 400 }}
                >
                  Min Win Rate: {goals.winRate ? `${goals.winRate}%` : 'Not set'}
                </MDTypography>
                {metrics.winRate !== null && goals.winRate ? (
                  <MDTypography
                    variant="caption"
                    color={isOnTarget('winRate', goals.winRate) ? 'success' : 'error'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'medium' }}
                  >
                    {isOnTarget('winRate', goals.winRate) ? (
                      <CheckCircle fontSize="small" sx={{ fontSize: 16 }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ fontSize: 16, color: theme.palette.error.main }} />
                    )}
                    {isOnTarget('winRate', goals.winRate) ? 'On Target' : 'Not On Target'}
                    ({metrics.winRate.toFixed(1)}%)
                  </MDTypography>
                ) : (
                  <MDTypography variant="caption" color="text">
                    {metrics.winRate === null ? 'No trades' : 'No target'}
                  </MDTypography>
                )}
              </MDBox>
            )}
            {/* Max Drawdown */}
            {goals.maxDrawdown !== undefined && goals.maxDrawdown !== null && (
              <MDBox
                display="flex"
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: '6px',
                  transition: 'transform 0.2s ease',
                  animation: 'fadeIn 0.3s ease-in-out 0.1s forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <MDTypography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: 400 }}
                >
                  Max Drawdown: {goals.maxDrawdown ? `${goals.maxDrawdown}%` : 'Not set'}
                </MDTypography>
                {metrics.maxDrawdown !== null && goals.maxDrawdown ? (
                  <MDTypography
                    variant="caption"
                    color={isOnTarget('maxDrawdown', goals.maxDrawdown) ? 'success' : 'error'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'medium' }}
                  >
                    {isOnTarget('maxDrawdown', goals.maxDrawdown) ? (
                      <CheckCircle fontSize="small" sx={{ fontSize: 16 }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ fontSize: 16, color: theme.palette.error.main }} />
                    )}
                    {isOnTarget('maxDrawdown', goals.maxDrawdown) ? 'On Target' : 'Not On Target'}
                    ({metrics.maxDrawdown.toFixed(1)}%)
                  </MDTypography>
                ) : (
                  <MDTypography variant="caption" color="text">
                    {metrics.maxDrawdown === null ? 'No trades' : 'No target'}
                  </MDTypography>
                )}
              </MDBox>
            )}
            {/* Min Trades Per Month */}
            {goals.minTradesPerMonth !== undefined && goals.minTradesPerMonth !== null && (
  <MDBox
    display="flex"
    alignItems="center"
    sx={{
      p: 1,
      borderRadius: '6px',
      transition: 'transform 0.2s ease',
      animation: 'fadeIn 0.3s ease-in-out 0.2s forwards',
      '@keyframes fadeIn': {
        from: { opacity: 0, transform: 'translateY(8px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      },
      '&:hover': {
        transform: 'translateY(-2px)',
      },
    }}
  >
    <MDTypography
      variant="body2"
      sx={{ flex: 1, fontWeight: 400 }}
    >
      Min Trades/Month: {goals.minTradesPerMonth ? goals.minTradesPerMonth : 'Not set'}
    </MDTypography>
    {metrics.tradesPerMonth !== null && goals.minTradesPerMonth ? (
      <MDTypography
        variant="caption"
        color={isOnTarget('tradesPerMonth', goals.minTradesPerMonth) ? 'success' : 'error'}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'medium' }}
      >
        {isOnTarget('tradesPerMonth', goals.minTradesPerMonth) ? (
          <CheckCircle fontSize="small" sx={{ fontSize: 16 }} />
        ) : (
          <Cancel fontSize="small" sx={{ fontSize: 16, color: theme.palette.error.main }} />
        )}
        {isOnTarget('tradesPerMonth', goals.minTradesPerMonth) ? 'On Target' : 'Not On Target'}
        ({metrics.tradesPerMonth})
      </MDTypography>
    ) : (
      <MDTypography variant="caption" color="text">
        {metrics.tradesPerMonth === null ? 'No trades' : 'No target'}
      </MDTypography>
    )}
  </MDBox>
)}
            {/* Max Trades Per Month */}
            {goals.maxTradesPerMonth !== undefined && goals.maxTradesPerMonth !== null && (
              <MDBox
                display="flex"
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: '6px',
                  transition: 'transform 0.2s ease',
                  animation: 'fadeIn 0.3s ease-in-out 0.3s forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <MDTypography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: 400 }}
                >
                  Max Trades/Month: {goals.maxTradesPerMonth ? goals.maxTradesPerMonth : 'Not set'}
                </MDTypography>
                {metrics.tradesPerMonth !== null && goals.maxTradesPerMonth ? (
                  <MDTypography
                    variant="caption"
                    color={isOnTarget('maxTradesPerMonth', goals.maxTradesPerMonth) ? 'success' : 'error'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'medium' }}
                  >
                    {isOnTarget('maxTradesPerMonth', goals.maxTradesPerMonth) ? (
                      <CheckCircle fontSize="small" sx={{ fontSize: 16 }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ fontSize: 16, color: theme.palette.error.main }} />
                    )}
                    {isOnTarget('maxTradesPerMonth', goals.maxTradesPerMonth) ? 'On Target' : 'Not On Target'}
                    ({metrics.tradesPerMonth})
                  </MDTypography>
                ) : (
                  <MDTypography variant="caption" color="text">
                    {metrics.tradesPerMonth === null ? 'No trades' : 'No target'}
                  </MDTypography>
                )}
              </MDBox>
            )}
            {/* Max Stop Loss */}
            {goals.stopLoss !== undefined && goals.stopLoss !== null && (
              <MDBox
                display="flex"
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: '6px',
                  transition: 'transform 0.2s ease',
                  animation: 'fadeIn 0.3s ease-in-out 0.4s forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <MDTypography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: 400 }}
                >
                  Max Stop Loss: {goals.stopLoss ? `${goals.stopLoss}%` : 'Not set'}
                </MDTypography>
                {metrics.stopLoss !== null && goals.stopLoss ? (
                  <MDTypography
                    variant="caption"
                    color={isOnTarget('stopLoss', goals.stopLoss) ? 'success' : 'error'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'medium' }}
                  >
                    {isOnTarget('stopLoss', goals.stopLoss) ? (
                      <CheckCircle fontSize="small" sx={{ fontSize: 16 }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ fontSize: 16, color: theme.palette.error.main }} />
                    )}
                    {isOnTarget('stopLoss', goals.stopLoss) ? 'On Target' : 'Not On Target'}
                    ({metrics.stopLoss.toFixed(1)}%)
                  </MDTypography>
                ) : (
                  <MDTypography variant="caption" color="text">
                    {metrics.stopLoss === null ? 'No trades' : 'No target'}
                  </MDTypography>
                )}
              </MDBox>
            )}
            {/* Min Risk-to-Reward Ratio */}
            {goals.minRiskRewardRatio !== undefined && goals.minRiskRewardRatio !== null && (
              <MDBox
                display="flex"
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: '6px',
                  transition: 'transform 0.2s ease',
                  animation: 'fadeIn 0.3s ease-in-out 0.5s forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <MDTypography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: 400 }}
                >
                  Min Risk-to-Reward Ratio: {goals.minRiskRewardRatio ? goals.minRiskRewardRatio.toFixed(2) : 'Not set'}
                </MDTypography>
                {metrics.riskRewardRatio !== null && goals.minRiskRewardRatio ? (
                  <MDTypography
                    variant="caption"
                    color={isOnTarget('riskRewardRatio', goals.minRiskRewardRatio) ? 'success' : 'error'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'medium' }}
                  >
                    {isOnTarget('riskRewardRatio', goals.minRiskRewardRatio) ? (
                      <CheckCircle fontSize="small" sx={{ fontSize: 16 }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ fontSize: 16, color: theme.palette.error.main }} />
                    )}
                    {isOnTarget('riskRewardRatio', goals.minRiskRewardRatio) ? 'On Target' : 'Not On Target'}
                    ({metrics.riskRewardRatio.toFixed(2)})
                  </MDTypography>
                ) : (
                  <MDTypography variant="caption" color="text">
                    {metrics.riskRewardRatio === null ? 'No trades' : 'No target'}
                  </MDTypography>
                )}
              </MDBox>
            )}
            {/* High-Confidence Win Rate */}
            {goals.highConfidenceWinRate !== undefined && goals.highConfidenceWinRate !== null && (
              <MDBox
                display="flex"
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: '6px',
                  transition: 'transform 0.2s ease',
                  animation: 'fadeIn 0.3s ease-in-out 0.6s forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <MDTypography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: 400 }}
                >
                  Min High-Confidence Win Rate: {goals.highConfidenceWinRate ? `${goals.highConfidenceWinRate}%` : 'Not set'}
                </MDTypography>
                {metrics.highConfidenceWinRate !== null && goals.highConfidenceWinRate ? (
                  <MDTypography
                    variant="caption"
                    color={isOnTarget('highConfidenceWinRate', goals.highConfidenceWinRate) ? 'success' : 'error'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'medium' }}
                  >
                    {isOnTarget('highConfidenceWinRate', goals.highConfidenceWinRate) ? (
                      <CheckCircle fontSize="small" sx={{ fontSize: 16 }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ fontSize: 16, color: theme.palette.error.main }} />
                    )}
                    {isOnTarget('highConfidenceWinRate', goals.highConfidenceWinRate) ? 'On Target' : 'Not On Target'}
                    ({metrics.highConfidenceWinRate.toFixed(1)}%)
                  </MDTypography>
                ) : (
                  <MDTypography variant="caption" color="text">
                    {metrics.highConfidenceWinRate === null ? 'No trades' : 'No target'}
                  </MDTypography>
                )}
              </MDBox>
            )}
            {/* Min Trade Score */}
            {goals.minTradeScore !== undefined && goals.minTradeScore !== null && (
              <MDBox
                display="flex"
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: '6px',
                  transition: 'transform 0.2s ease',
                  animation: 'fadeIn 0.3s ease-in-out 0.7s forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <MDTypography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: 400 }}
                >
                  Min Trade Score: {goals.minTradeScore ? goals.minTradeScore : 'Not set'}
                </MDTypography>
                {metrics.tradeScore !== null && goals.minTradeScore ? (
                  <MDTypography
                    variant="caption"
                    color={isOnTarget('tradeScore', goals.minTradeScore) ? 'success' : 'error'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'medium' }}
                  >
                    {isOnTarget('tradeScore', goals.minTradeScore) ? (
                      <CheckCircle fontSize="small" sx={{ fontSize: 16 }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ fontSize: 16, color: theme.palette.error.main }} />
                    )}
                    {isOnTarget('tradeScore', goals.minTradeScore) ? 'On Target' : 'Not On Target'}
                    ({metrics.tradeScore.toFixed(1)})
                  </MDTypography>
                ) : (
                  <MDTypography variant="caption" color="text">
                    {metrics.tradeScore === null ? 'No trades' : 'No target'}
                  </MDTypography>
                )}
              </MDBox>
            )}
            {/* Min Unique Symbols */}
            {goals.minUniqueSymbols !== undefined && goals.minUniqueSymbols !== null && (
              <MDBox
                display="flex"
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: '6px',
                  transition: 'transform 0.2s ease',
                  animation: 'fadeIn 0.3s ease-in-out 0.8s forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <MDTypography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: 400 }}
                >
                  Min Unique Symbols: {goals.minUniqueSymbols ? goals.minUniqueSymbols : 'Not set'}
                </MDTypography>
                {metrics.uniqueSymbols !== null && goals.minUniqueSymbols ? (
                  <MDTypography
                    variant="caption"
                    color={isOnTarget('uniqueSymbols', goals.minUniqueSymbols) ? 'success' : 'error'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'medium' }}
                  >
                    {isOnTarget('uniqueSymbols', goals.minUniqueSymbols) ? (
                      <CheckCircle fontSize="small" sx={{ fontSize: 16 }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ fontSize: 16, color: theme.palette.error.main }} />
                    )}
                    {isOnTarget('uniqueSymbols', goals.minUniqueSymbols) ? 'On Target' : 'Not On Target'}
                    ({metrics.uniqueSymbols})
                  </MDTypography>
                ) : (
                  <MDTypography variant="caption" color="text">
                    {metrics.uniqueSymbols === null ? 'No trades' : 'No target'}
                  </MDTypography>
                )}
              </MDBox>
            )}
            {/* Max Unique Symbols */}
            {goals.maxUniqueSymbols !== undefined && goals.maxUniqueSymbols !== null && (
              <MDBox
                display="flex"
                alignItems="center"
                sx={{
                  p: 1,
                  borderRadius: '6px',
                  transition: 'transform 0.2s ease',
                  animation: 'fadeIn 0.3s ease-in-out 0.9s forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <MDTypography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: 400 }}
                >
                  Max Unique Symbols: {goals.maxUniqueSymbols ? goals.maxUniqueSymbols : 'Not set'}
                </MDTypography>
                {metrics.uniqueSymbols !== null && goals.maxUniqueSymbols ? (
                  <MDTypography
                    variant="caption"
                    color={isOnTarget('maxUniqueSymbols', goals.maxUniqueSymbols) ? 'success' : 'error'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'medium' }}
                  >
                    {isOnTarget('maxUniqueSymbols', goals.maxUniqueSymbols) ? (
                      <CheckCircle fontSize="small" sx={{ fontSize: 16 }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ fontSize: 16, color: theme.palette.error.main }} />
                    )}
                    {isOnTarget('maxUniqueSymbols', goals.maxUniqueSymbols) ? 'On Target' : 'Not On Target'}
                    ({metrics.uniqueSymbols})
                  </MDTypography>
                ) : (
                  <MDTypography variant="caption" color="text">
                    {metrics.uniqueSymbols === null ? 'No trades' : 'No target'}
                  </MDTypography>
                )}
              </MDBox>
            )}
          </MDBox>
        )}
      </CardContent>
    </Card>
  );
};

const DiscountCodeInput = ({ 
  onApplyCode, 
  validating, 
  error, 
  discountInfo 
}) => {
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  

  return (
    <MDBox mb={3}>
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <TextField
          label="Discount Code"
          fullWidth
          inputRef={inputRef}
          disabled={validating}
          error={!!error}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          InputLabelProps={{
            style: { marginTop: 5 }  // added margin to prevent cutoff
          }}
          sx={{ "& .MuiInputBase-root": { bgcolor: "transparent" } }}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && inputRef.current.value) {
              onApplyCode(inputRef.current.value);
            }
          }}
        />
        <MDButton 
          variant="gradient" 
          color="info" 
          onClick={() => onApplyCode(inputRef.current.value)}
          disabled={validating || !inputValue}
          sx={{ minWidth: '100px' }}
        >
          {validating ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Apply'
          )}
        </MDButton>
      </MDBox>
      {discountInfo && (
        <MDTypography variant="caption" color="success" mt={1} display="block">
          {discountInfo.type === 'percentage' 
            ? `${discountInfo.value}% discount applied!`
            : `$${discountInfo.value} discount applied!`}
        </MDTypography>
      )}
      {error && (
        <MDTypography color="error" variant="caption" mt={1} display="block">
          {error}
        </MDTypography>
      )}
    </MDBox>
  );
};

<style jsx global>{`
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
    100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
  }
`}</style>

const Overview = () => {
  const navigate = useNavigate();
  const [profileInfo, setProfileInfo] = useState({
    fullName: "",
    email: "",
    location: "",
    tradingStrategy: "",
    aboutMe: "",
    tradingGoals: "",
  });
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicURL, setProfilePicURL] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();
  const [modalMessage, setModalMessage] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [inputDiscountCode, setInputDiscountCode] = useState('');

  const [hasHadTrial, setHasHadTrial] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState({
    plan: "",
    subscriptionID: "",
    isDemo: false,
    isGifted: false,
    expiresAt: null,
    paypalSubscriptionId: "",
    stripeSubscriptionId: "", // Added for Stripe
  });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [coachLogs, setCoachLogs] = useState([]);
  const [coachLogsLoading, setCoachLogsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [upgradeDiscountCode, setUpgradeDiscountCode] = useState('');
  const [upgradeDiscountInfo, setUpgradeDiscountInfo] = useState(null);
  const [validatingUpgradeCode, setValidatingUpgradeCode] = useState(false);
  const [checklists, setChecklists] = useState([]);
  const [newChecklistName, setNewChecklistName] = useState('');
  const [newItem, setNewItem] = useState({ description: '', weight: '' });
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [pendingPermissionChange, setPendingPermissionChange] = useState(null);
  const [isChecklistEnabled, setIsChecklistEnabled] = useState(false);
  const [coachGoals, setCoachGoals] = useState(null);
  const [teamLogoUrl, setTeamLogoUrl] = useState(""); // New state for team logo
  const [teamName, setTeamName] = useState(""); // New state for team name
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItem, setEditingItem] = useState({ description: '', weight: '' });
  const [userMetrics, setUserMetrics] = useState({
    winRate: null,
    maxDrawdown: null,
    tradesPerMonth: null,
    stopLoss: null, // New metric
  });
  const [metricsLoading, setMetricsLoading] = useState(false);
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  const [upgradeError, setUpgradeError] = useState(null);
  const GIFTED_ACCOUNTS = [
    'BZqRPWxemaMLU4oFhGdnyuzapOh1',
    'tnfnwSzbqzRksTjo82bZAO0d4Ly2',
    '2iSq7W3AttOQoR6jPiAPUwvbyqF3',
    'ev3xyjENZ0fZfSLbz7KYpvQHb613',
    '4JkJV2u5InQeearkcPKfFkbC73s2'
  ];

  // Fetch team logo and name in real-time
  useEffect(() => {
    let unsubscribeLogo, unsubscribeTeam;
    if (profileInfo.teamId) {
      console.log("Overview: Setting up team listeners for teamId:", profileInfo.teamId);

      // Listen for team logo
      const logoDocRef = doc(db, "teamLogos", profileInfo.teamId);
      unsubscribeLogo = onSnapshot(
        logoDocRef,
        (logoDoc) => {
          if (logoDoc.exists()) {
            const logoUrl = logoDoc.data().logoUrl || "";
            setTeamLogoUrl(logoUrl);
            console.log("Overview: Real-time team logo URL:", logoUrl);
          } else {
            setTeamLogoUrl("");
            console.log("Overview: No team logo found for teamId:", profileInfo.teamId);
          }
        },
        (error) => {
          console.error("Overview: Error listening to team logo:", error);
          setTeamLogoUrl("");
        }
      );

      // Listen for team name
      const teamDocRef = doc(db, "teams", profileInfo.teamId);
      unsubscribeTeam = onSnapshot(
        teamDocRef,
        (teamDoc) => {
          if (teamDoc.exists()) {
            const teamData = teamDoc.data();
            setTeamName(teamData.name || "");
            console.log("Overview: Real-time team name:", teamData.name);
          } else {
            setTeamName("");
            console.log("Overview: No team found for teamId:", profileInfo.teamId);
          }
        },
        (error) => {
          console.error("Overview: Error listening to team data:", error);
          setTeamName("");
        }
      );
    } else {
      setTeamLogoUrl("");
      setTeamName("");
      console.log("Overview: No teamId in profileInfo");
    }

    return () => {
      if (unsubscribeLogo) {
        unsubscribeLogo();
        console.log("Overview: Unsubscribed from logo listener");
      }
      if (unsubscribeTeam) {
        unsubscribeTeam();
        console.log("Overview: Unsubscribed from team listener");
      }
    };
  }, [profileInfo.teamId]);

  useEffect(() => {
    let unsubscribe;
    if (auth.currentUser?.uid && profileInfo?.teamId) {
      setCoachLogsLoading(true);
      const logsQuery = query(
        collection(db, `Users/${auth.currentUser.uid}/coachLogs/${profileInfo.teamId}/logs`),
        orderBy('createdAt', 'desc')
      );
  
      unsubscribe = onSnapshot(
        logsQuery,
        (snapshot) => {
          try {
            const logsData = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                note: data.note,
                createdAt: data.createdAt?.toDate()?.toISOString() || new Date().toISOString(),
                updatedAt: data.updatedAt?.toDate()?.toISOString() || null,
                createdBy: data.createdBy || 'Unknown',
                updatedBy: data.updatedBy || null,
                teamId: data.teamId || profileInfo.teamId,
              };
            });
            setCoachLogs(logsData);
            setCoachLogsLoading(false);
            console.log("Overview: Fetched coach logs:", logsData);
          } catch (error) {
            console.error("Overview: Error processing coach logs:", error);
            setCoachLogs([]);
            setCoachLogsLoading(false);
            setError("Failed to load coach logs");
          }
        },
        (error) => {
          console.error("Overview: Error fetching coach logs:", error);
          setCoachLogs([]);
          setCoachLogsLoading(false);
          setError("Failed to load coach logs");
        }
      );
    } else {
      setCoachLogs([]);
      setCoachLogsLoading(false);
      console.log("Overview: No user ID or team ID, skipping coach logs fetch");
    }
  
    return () => {
      if (unsubscribe) unsubscribe();
      console.log("Overview: Unsubscribed from coach logs listener");
    };
  }, [auth.currentUser?.uid, profileInfo?.teamId]);

  // Existing useEffect for user data (modified to avoid duplicate team name fetch)
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDocRef = doc(db, "Users", user.uid);
          const subscriptionDocRef = doc(db, "subscriptions", user.uid);
          const checklistsRef = collection(db, "Users", user.uid, "checklists");

          const [userDoc, subscriptionDoc, checklistsSnapshot] = await Promise.all([
            getDoc(userDocRef),
            getDoc(subscriptionDocRef),
            getDocs(checklistsRef),
          ]);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const subData = subscriptionDoc.exists() ? subscriptionDoc.data() : {};

            // Set profile info with team permissions
            const profileData = {
              fullName: userData.profileInfo?.fullName || userData.fullName || "",
              email: userData.profileInfo?.email || userData.email || "",
              location: userData.profileInfo?.location || "",
              tradingStrategy: userData.profileInfo?.tradingStrategy || "",
              aboutMe: userData.profileInfo?.aboutMe || "",
              tradingGoals: userData.profileInfo?.tradingGoals || "",
              teamId: userData.profileInfo?.teamId || null,
              teamPermissions: userData.profileInfo?.teamPermissions || {},
            };
            setProfileInfo(profileData);
            setProfilePicURL(userData.profilePicURL || "");

            // Set coach goals and risk settings
            if (profileData.teamId) {
              const teamGoals = userData.profileInfo?.coachGoals?.[profileData.teamId] || {};
              const teamRiskSettings = userData.profileInfo?.coachRiskSettings?.[profileData.teamId] || {};
              setCoachGoals({
                ...teamGoals,
                stopLoss: teamRiskSettings.stopLoss || null,
              });
            }

            // Set hasHadTrial
            setHasHadTrial(userData.hasHadTrial || subData.hasHadTrial || false);

            // Check for lifetime gifted accounts
            const isLifetimeGift = GIFTED_ACCOUNTS.includes(user.uid);
            if (isLifetimeGift && (!userData.isGifted || !subData.isGifted)) {
              await updateDoc(userDocRef, {
                isGifted: true,
                updatedAt: new Date().toISOString(),
              });
              if (subscriptionDoc.exists()) {
                await updateDoc(subscriptionDocRef, {
                  isGifted: true,
                  updatedAt: new Date().toISOString(),
                });
              }
            }

            // Build subscription info
            const subInfo = {
              plan: userData.plan || subData.plan || "",
              subscriptionID: userData.subscriptionID || subData.subscriptionID || "",
              isDemo: userData.isDemo || subData.isDemo || false,
              demoUID: userData.demoUID || "",
              originalEmail: userData.originalEmail || "",
              isGifted: isLifetimeGift || userData.isGifted || subData.isGifted || false,
              is3MonthGift: userData.is3MonthGift || subData.is3MonthGift || false,
              giftType: userData.giftType || subData.giftType || (isLifetimeGift ? "Lifetime Access" : null),
              subscriptionName: userData.subscriptionName || subData.subscriptionName || null,
              giftDuration: userData.giftDuration || subData.giftDuration || null,
              hasHadTrial: userData.hasHadTrial || subData.hasHadTrial || false,
              expiresAt: userData.expiresAt || subData.planDetails?.endDate || null,
              convertToDemoAt: subData.convertToDemoAt ? new Date(subData.convertToDemoAt).toISOString() : null,
              type: subData.type || "",
              status: subData.status || "",
              paypalSubscriptionId: subData.paypalSubscriptionId || "",
              stripeSubscriptionId: subData.stripeSubscriptionId || "", // Added for Stripe
              planDetails: subData.planDetails || {},
            };
            setSubscriptionInfo(subInfo);

          // Calculate user metrics if teamId exists
          if (profileData.teamId) {
            setMetricsLoading(true);
            try {
              const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30));
              const thirtyDaysAgoString = thirtyDaysAgo.toISOString().split('T')[0]; // e.g., "2025-04-22"
              let totalCapital = 0;
              let totalTradesThisMonth = 0;
              let totalWins = 0;
              let totalValidTrades = 0;
              let maxDrawdown = 0;
              let totalStopLoss = 0;
              let stopLossTradeCount = 0;
              let riskRewardRatio = 0;
              let highConfidenceWinRate = 0;
              let tradeScore = 0;
              let uniqueSymbols = 0;

              console.log("Metrics: Calculating for thirtyDaysAgo:", thirtyDaysAgoString);

              // Fetch trade accounts
              const accountsQuery = query(collection(db, `Users/${user.uid}/tradeAccounts`));
              const accountsDocs = await getDocs(accountsQuery);
              const tradeAccountIds = [...new Set(accountsDocs.docs.map((accountDoc) => accountDoc.id))];
              console.log("Metrics: Found trade accounts:", tradeAccountIds);

              // Calculate total capital and collect trades
              const allTrades = [];
              for (const tradeAccountId of tradeAccountIds) {
                const capitalPath = `Users/${user.uid}/tradeAccounts/${tradeAccountId}/capital`;
                const capitalQuery = query(collection(db, capitalPath));
                const capitalDocs = await getDocs(capitalQuery);
                let deposits = 0;
                let withdrawals = 0;
                let returns = 0;

                capitalDocs.forEach((capitalDoc) => {
                  const data = capitalDoc.data();
                  deposits += Number(data.deposit) || 0;
                  withdrawals += Number(data.withdrawal) || 0;
                });

                const tradesQuery = query(
                  collection(db, `Users/${user.uid}/tradeAccounts/${tradeAccountId}/trades`),
                  where('date', '>=', thirtyDaysAgoString)
                );
                const tradesDocs = await getDocs(tradesQuery);
                console.log(`Metrics: Trades for ${tradeAccountId}:`, tradesDocs.docs.length);

                if (!tradesDocs.empty) {
                  const trades = tradesDocs.docs
                    .map((t) => t.data())
                    .filter((t) => t.returns !== undefined && t.status === "Closed");
                  allTrades.push(...trades);
                  returns = trades.reduce((sum, trade) => sum + (Number(trade.returns) || 0), 0);
                }

                totalCapital += deposits - withdrawals + returns;
                console.log(`Metrics: Capital for ${tradeAccountId}: deposits=${deposits}, withdrawals=${withdrawals}, returns=${returns}, totalCapital=${totalCapital}`);
              }

              // If no capital data, estimate from trades
              if (totalCapital === 0) {
                console.log("Metrics: No capital data, estimating from trades");
                totalCapital = allTrades.reduce((sum, trade) => {
                  return sum + (Number(trade.returns) || 0) + (Number(trade.riskAmount) || 0);
                }, 0) || 1000;
              }

              // Process trades for metrics
              if (allTrades.length > 0) {
                const validTrades = allTrades;
                totalWins = validTrades.filter((t) => Number(t.returns) > 0).length;
                totalValidTrades = validTrades.length;
                totalTradesThisMonth = validTrades.length;

                // Calculate stop loss
                const tradesWithRisk = validTrades.filter((t) => t.riskAmount !== undefined && t.riskAmount !== null);
                const accountRiskSum = tradesWithRisk.reduce((sum, trade) => {
                  const riskAmount = parseFloat(trade.riskAmount) || 0;
                  return sum + (totalCapital > 0 ? (riskAmount / totalCapital) * 100 : 0);
                }, 0);
                totalStopLoss += accountRiskSum;
                stopLossTradeCount += tradesWithRisk.length;
                console.log(`Metrics: Stop loss: riskSum=${accountRiskSum}, tradesWithRisk=${tradesWithRisk.length}`);

                // Calculate risk-to-reward ratio
                const tradesWithRR = validTrades.filter((t) => t.riskAmount && t.returns !== undefined && t.status === "Closed");
                const rrRatios = tradesWithRR
                  .filter((t) => t.returns > 0 && t.riskAmount > 0)
                  .map((t) => t.returns / t.riskAmount);
                riskRewardRatio = rrRatios.length > 0 ? rrRatios.reduce((sum, r) => sum + r, 0) / rrRatios.length : 0;

                // Calculate high-confidence win rate
                const highConfidenceTrades = validTrades.filter((t) => t.tradeConfidence === 'High');
                const highConfidenceWins = highConfidenceTrades.filter((t) => t.returns > 0).length;
                highConfidenceWinRate = highConfidenceTrades.length > 0 ? (highConfidenceWins / highConfidenceTrades.length) * 100 : 0;

                // Calculate trade score
                const tradeScores = validTrades
                  .map((t) => (t.fxbTradeScore ? parseInt(t.fxbTradeScore.split('/')[0]) : 0))
                  .filter((score) => score > 0);
                tradeScore = tradeScores.length > 0 ? tradeScores.reduce((sum, s) => sum + s, 0) / tradeScores.length : 0;

                // Calculate unique symbols
                uniqueSymbols = [...new Set(validTrades.map((t) => t.symbol))].length;

                // Calculate max drawdown
                allTrades.sort((a, b) => new Date(a.date + 'T' + a.entryTime) - new Date(b.date + 'T' + b.entryTime));
                console.log(`Metrics: Sorted trades for drawdown (all accounts):`, allTrades.length);

                let equity = totalCapital || 1000;
                let peak = equity;
                let trough = equity;

                for (const trade of allTrades) {
                  equity += Number(trade.returns) || 0;
                  peak = Math.max(peak, equity);
                  trough = Math.min(trough, equity);
                  const drawdown = peak > 0 ? ((peak - trough) / peak) * 100 : 0;
                  maxDrawdown = Math.max(maxDrawdown, drawdown);
                }
                console.log(`Metrics: Drawdown (all accounts): peak=${peak}, trough=${trough}, maxDrawdown=${maxDrawdown}`);
              }

              const avgWinRate = totalValidTrades > 0 ? Math.min((totalWins / totalValidTrades) * 100, 100) : 0;
              const avgStopLoss = stopLossTradeCount > 0 ? totalStopLoss / stopLossTradeCount : null;

              setUserMetrics({
                winRate: avgWinRate,
                maxDrawdown: totalTradesThisMonth > 0 ? maxDrawdown : 0,
                tradesPerMonth: totalTradesThisMonth,
                stopLoss: avgStopLoss,
                riskRewardRatio: totalValidTrades > 0 ? riskRewardRatio : null,
                highConfidenceWinRate: totalValidTrades > 0 ? highConfidenceWinRate : null,
                tradeScore: totalValidTrades > 0 ? tradeScore : null,
                uniqueSymbols: totalValidTrades > 0 ? uniqueSymbols : null,
              });
              console.log("Metrics: Final - ", {
                winRate: avgWinRate,
                maxDrawdown,
                tradesPerMonth: totalTradesThisMonth,
                stopLoss: avgStopLoss,
                riskRewardRatio,
                highConfidenceWinRate,
                tradeScore,
                uniqueSymbols,
              });
            } catch (error) {
              console.error("Metrics: Error calculating user metrics:", error);
              setUserMetrics({
                winRate: totalValidTrades > 0 ? Math.min((totalWins / totalValidTrades) * 100, 100) : null,
                maxDrawdown: totalTradesThisMonth > 0 ? maxDrawdown : null,
                tradesPerMonth: totalTradesThisMonth || null,
                stopLoss: stopLossTradeCount > 0 ? totalStopLoss / stopLossTradeCount : null,
                riskRewardRatio: null,
                highConfidenceWinRate: null,
                tradeScore: null,
                uniqueSymbols: null,
              });
            } finally {
              setMetricsLoading(false);
            }
          }

          // Handle checklists
          const fetchedChecklists = checklistsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) || [];
          setChecklists(fetchedChecklists);
          setIsChecklistEnabled(userData?.checklistEnabled || false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load profile data");
      } finally {
        setLoading(false);
      }
    }
  });

  return () => unsubscribe();
}, []);

// New handler for permission toggle
const handlePermissionToggle = (event) => {
  const newValue = event.target.checked;
  setPendingPermissionChange(newValue);
  setShowPermissionModal(true);
};

const confirmPermissionChange = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (user && profileInfo.teamId && pendingPermissionChange !== null) {
    try {
      const userDocRef = doc(db, "Users", user.uid);
      await updateDoc(userDocRef, {
        [`profileInfo.teamPermissions.${profileInfo.teamId}`]: pendingPermissionChange,
      });
      setProfileInfo({
        ...profileInfo,
        teamPermissions: {
          ...profileInfo.teamPermissions,
          [profileInfo.teamId]: pendingPermissionChange,
        },
      });
      setModalMessage(
        pendingPermissionChange
          ? "Coach permission enabled successfully!"
          : "Coach permission disabled successfully!"
      );
      setModalOpen(true);
    } catch (error) {
      console.error("Error updating permission:", error);
      setError("Failed to update coach permission");
    } finally {
      setShowPermissionModal(false);
      setPendingPermissionChange(null);
    }
  }
};

const handleEditChecklistItem = (index) => {
  const item = checklists[0].items[index];
  setEditingIndex(index);
  setEditingItem({ ...item });
};

const handleSaveEditedItem = () => {
  setChecklists(prev => {
    const checklist = prev[0];
    const items = [...checklist.items];

    const otherWeights = items.reduce((sum, item, i) => {
      return i === editingIndex ? sum : sum + Number(item.weight);
    }, 0);

    const newTotal = otherWeights + Number(editingItem.weight);

    if (newTotal > 100) {
      setModalMessage('Total weight cannot exceed 100%');
      setModalOpen(true);
      return prev;
    }

    items[editingIndex] = {
      description: editingItem.description,
      weight: Number(editingItem.weight)
    };

    return [{ ...checklist, items }];
  });

  setEditingIndex(null);
  setEditingItem({ description: '', weight: '' });
};

const handleCancelSubscription = async () => {
  try {
    setLoading(true);
    setError(null);

    const auth = getAuth();
    const user = auth.currentUser;
    const subscriptionId = subscriptionInfo.paypalSubscriptionId || subscriptionInfo.stripeSubscriptionId;

    if (!user || !subscriptionId) {
      throw new Error('No active subscription found or user not authenticated');
    }

    console.log('Attempting to cancel subscription:', {
      userId: user.uid,
      subscriptionId,
      paymentMethod: subscriptionInfo.paypalSubscriptionId ? 'paypal' : 'stripe',
      apiUrl: `${process.env.REACT_APP_API_URL}/api/cancel-subscription`,
    });

    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Origin': window.location.origin,
      },
      body: JSON.stringify({
        subscriptionId,
        userId: user.uid,
        paymentMethod: subscriptionInfo.paypalSubscriptionId ? 'paypal' : 'stripe',
      }),
    });

    console.log('Cancel subscription response status:', response.status);

    if (!response.ok) {
      let errorMessage = 'Failed to cancel subscription';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (jsonError) {
        console.error('Error parsing JSON response:', jsonError);
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('Cancel subscription response data:', data);

    // Update local state to reflect cancellation
    setSubscriptionInfo({
      ...subscriptionInfo,
      status: 'cancelled',
      convertToDemoAt: data.nextBillingDate || new Date().toISOString(),
    });

    setShowCancelModal(false);
    setModalMessage(
      `Subscription cancelled successfully. Premium access continues until ${new Date(
        data.nextBillingDate
      ).toLocaleDateString()}.`
    );
    setModalOpen(true);
  } catch (error) {
    console.error('Failed to cancel subscription:', error);
    setError(error.message || 'Failed to cancel subscription. Please try again.');
  } finally {
    setLoading(false);
  }
};
  const validateUpgradeDiscountCode = async (code) => {
    try {
      setValidatingUpgradeCode(true);
      setUpgradeError(null);
      
      const discountCodesCollection = collection(db, 'discountCodes');
      const codeRef = await getDocs(
        query(
          discountCodesCollection,
          where('code', '==', code.toUpperCase()),
          where('active', '==', true)
        )
      );
  
      if (!codeRef.empty) {
        const codeData = codeRef.docs[0].data();
        
        if (codeData.validUntil && new Date() > new Date(codeData.validUntil.seconds * 1000)) {
          setUpgradeError('This discount code has expired');
          return false;
        }
        
        if (codeData.currentUses >= codeData.maxUses) {
          setUpgradeError('This discount code has reached its maximum uses');
          return false;
        }
        
        if (codeData.plans && (
          codeData.plans.includes('all_plans') || 
          codeData.plans.includes('monthly_plan') || 
          codeData.plans.includes('annual_plan') || 
          codeData.plans.includes('trial_upgrade_plan')
        )) {
          const modifiedCodeData = { ...codeData };
          
          if (codeData.plans.includes('trial_upgrade_plan')) {
            // Override for TRIAL_UPGRADE: use TRIAL_UPGRADE PayPal plan
            modifiedCodeData.monthlyPlanId = MEMBERSHIP_PLANS.TRIAL_UPGRADE.paypalPlanId;
            modifiedCodeData.trialUpgradePrice = MEMBERSHIP_PLANS.TRIAL_UPGRADE.price; // $5
            modifiedCodeData.isTrialUpgrade = true;
          } else if (codeData.type === 'percentage') {
            const value = parseInt(codeData.value);
            if (codeData.plans.includes('monthly_plan') || codeData.plans.includes('all_plans')) {
              modifiedCodeData.monthlyPlanId = MEMBERSHIP_PLANS.MONTHLY.discountedPlans[value];
            }
            if (codeData.plans.includes('annual_plan') || codeData.plans.includes('all_plans')) {
              modifiedCodeData.annualPlanId = MEMBERSHIP_PLANS.ANNUAL.discountedPlans[value];
            }
          }
          
          setUpgradeDiscountInfo(modifiedCodeData);
          setUpgradeDiscountCode(code.toUpperCase());
          return true;
        } else {
          setUpgradeError('This discount code does not apply to any available plans');
          return false;
        }
      }
      
      setUpgradeDiscountInfo(null);
      setUpgradeError('Invalid discount code');
      return false;
    } catch (error) {
      console.error('Error validating code:', error);
      setUpgradeError('Error validating discount code: ' + error.message);
      return false;
    } finally {
      setValidatingUpgradeCode(false);
    }
  };
  
  const handleChangePlan = async (newPlanType) => {
    try {
      const auth = getAuth();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/change-subscription-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscriptionId: subscriptionInfo.paypalSubscriptionId, // Fix this
          userId: auth.currentUser.uid,
          newPlanType,
          planId: newPlanType === 'monthly' ? 
            MEMBERSHIP_PLANS.MONTHLY.paypalPlanId : 
            MEMBERSHIP_PLANS.ANNUAL.paypalPlanId
        })
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error details:', errorData);
        throw new Error(errorData.error || 'Failed to change subscription plan');
      }
      const { nextBillingTime } = await response.json();
  
      // Update Firestore
      await updateDoc(doc(db, 'subscriptions', auth.currentUser.uid), {
        type: newPlanType,
        plan: `${newPlanType}_plan`,
        planDetails: {
          cost: MEMBERSHIP_PLANS[newPlanType.toUpperCase()].price,
          originalCost: MEMBERSHIP_PLANS[newPlanType.toUpperCase()].price,
          nextBillingDate: nextBillingTime,
          discountApplied: null
        },
        updatedAt: new Date().toISOString()
      });
  
      // Update local state
      setSubscriptionInfo(prev => ({
        ...prev,
        type: newPlanType,
        plan: `${newPlanType}_plan`,
        planDetails: {
          ...prev.planDetails,
          cost: MEMBERSHIP_PLANS[newPlanType.toUpperCase()].price,
          originalCost: MEMBERSHIP_PLANS[newPlanType.toUpperCase()].price,
          nextBillingDate: nextBillingTime
        }
      }));
  
      setShowChangePlanModal(false);
      setModalMessage('Subscription plan updated successfully');
      setModalOpen(true);
      
      window.location.reload();
    } catch (error) {
      console.error('Error changing subscription plan:', error);
      setError(error.message || 'Failed to change subscription plan');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileInfo({
      ...profileInfo,
      [name]: value,
    });
  };

  const handleTradingStrategyChange = (e) => {
    setProfileInfo({
      ...profileInfo,
      tradingStrategy: e.htmlValue,
    });
  };

  const handleAboutMeChange = (e) => {
    setProfileInfo({
      ...profileInfo,
      aboutMe: e.htmlValue,
    });
  };

  const handleTradingGoalsChange = (e) => {
    setProfileInfo({
      ...profileInfo,
      tradingGoals: e.htmlValue,
    });
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files[0];
    setProfilePic(file);

    const storage = getStorage();
    const storageRef = ref(storage, `profilePictures/${file.name}`);
    
    try {
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      setProfilePicURL(url);
    } catch (error) {
      console.error("Error uploading file:", error);
      setError("Failed to upload profile picture");
    }
  };

  const handleManageStripeSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
  
      const auth = getAuth();
      const user = auth.currentUser;
  
      if (!user) {
        throw new Error('User not authenticated');
      }
  
      // Fetch subscription data from Firestore
      const subscriptionDoc = await getDoc(doc(db, 'subscriptions', user.uid));
      if (!subscriptionDoc.exists()) {
        throw new Error('No subscription found for this user');
      }
  
      const subscriptionData = subscriptionDoc.data();
      const stripeCustomerId = subscriptionData.stripeCustomerId;
  
      if (!stripeCustomerId) {
        throw new Error('No Stripe customer ID found for this user');
      }
  
      console.log('Creating Stripe Customer Portal session:', {
        userId: user.uid,
        stripeCustomerId,
        apiUrl: `${process.env.REACT_APP_API_URL}/api/create-portal-session`,
      });
  
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
        },
        body: JSON.stringify({
          userId: user.uid,
          stripeCustomerId
        }),
      });
  
      console.log('Create portal session response status:', response.status);
  
      if (!response.ok) {
        let errorMessage = 'Failed to create portal session';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (jsonError) {
          console.error('Error parsing JSON response:', jsonError);
        }
        throw new Error(errorMessage);
      }
  
      const data = await response.json();
      console.log('Create portal session response data:', data);
  
      // Redirect to the Stripe Customer Portal
      window.location.href = data.url;
      setShowChangePlanModal(false);
    } catch (error) {
      console.error('Error creating Stripe portal session:', error);
      setError(error.message || 'Failed to create portal session. Please try again.');
      setShowChangePlanModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = () => {
    if (subscriptionInfo.stripeSubscriptionId) {
      handleManageStripeSubscription();
    } else if (subscriptionInfo.paypalSubscriptionId) {
      console.log('Managing PayPal subscription:', subscriptionInfo);
      window.open('https://www.paypal.com/myaccount/autopay/', '_blank');
    }
  };

// Update handleUpgradeAccount
const handleUpgradeAccount = () => {
  setShowUpgradeModal(true);
};
const handleTrialUpgrade = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    const now = new Date();
    
    // Create subscription data for trial using the same structure as signup page
    const subscriptionData = {
      userId: user.uid,
      type: 'monthly', // Match signup page - monthly billing after trial
      plan: MEMBERSHIP_PLANS.TRIAL.id,
      status: 'active',
      isTrial: true,
      hasHadTrial: true,
      isDemo: false,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      expiresAt: new Date(now.getTime() + (MEMBERSHIP_PLANS.TRIAL.duration * 24 * 60 * 60 * 1000)).toISOString(),
      paypalSubscriptionId: null, // No PayPal ID since no payment required for trial
      stripeSubscriptionId: null, // Ensure Stripe ID is null for trial
      planDetails: {
        cost: 0,
        originalCost: MEMBERSHIP_PLANS.MONTHLY.price,
        startDate: now.toISOString(),
        endDate: new Date(now.getTime() + (MEMBERSHIP_PLANS.TRIAL.duration * 24 * 60 * 60 * 1000)).toISOString(),
        nextBillingDate: new Date(now.getTime() + (MEMBERSHIP_PLANS.TRIAL.duration * 24 * 60 * 60 * 1000)).toISOString(),
        trialEndDate: new Date(now.getTime() + (MEMBERSHIP_PLANS.TRIAL.duration * 24 * 60 * 60 * 1000)).toISOString(),
        regularPlanId: MEMBERSHIP_PLANS.MONTHLY.id
      }
    };

    // Update subscription in Firestore
    await updateDoc(doc(db, 'subscriptions', user.uid), subscriptionData);

    // Clean up demo attributes and update Users collection
    await updateDoc(doc(db, 'Users', user.uid), {
      email: profileInfo.email.replace('DEMO_', ''),
      isDemo: false,
      demoUID: null,
      plan: MEMBERSHIP_PLANS.TRIAL.id,
      hasHadTrial: true,
      isTrial: true,
      expiresAt: new Date(now.getTime() + (MEMBERSHIP_PLANS.TRIAL.duration * 24 * 60 * 60 * 1000)).toISOString(),
      originalEmail: null,
      updatedAt: now.toISOString()
    });

    // Send notification about trial upgrade
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
        },
        body: JSON.stringify({
          adminEmail: {
            to: 'support@fxbullsheet.com',
            userInfo: {
              fullName: profileInfo.fullName,
              email: profileInfo.email,
              plan: MEMBERSHIP_PLANS.TRIAL.id,
              isTrial: true
            }
          },
          userEmail: {
            to: profileInfo.email,
            fullName: profileInfo.fullName,
            planInfo: {
              plan: 'Trial Plan',
              cost: 0,
              nextBillingDate: new Date(now.getTime() + (MEMBERSHIP_PLANS.TRIAL.duration * 24 * 60 * 60 * 1000)).toISOString(),
            }
          }
        })
      });
    } catch (emailError) {
      console.error('Failed to send notification emails:', emailError);
    }

    setShowUpgradeModal(false);
    setModalMessage('Your account has been upgraded to a 7-day free trial!');
    setModalOpen(true);

    // Update local state
    setSubscriptionInfo({
      ...subscriptionInfo,
      ...subscriptionData,
      isDemo: false,
      hasHadTrial: true,
      isTrial: true
    });

    // Reload page to update UI
    window.location.reload();
  } catch (err) {
    console.error('Failed to upgrade to trial:', err);
    setError('Failed to upgrade to trial. Please try again.');
  }
};
// Add to the existing useEffect that fetches user data

// Add plan selection handler
const handlePlanSelection = (planType) => {
  sessionStorage.setItem('upgradeInfo', JSON.stringify({
    fromDemo: true,
    email: profileInfo.email,
    fullName: profileInfo.fullName,
    selectedPlan: planType
  }));
  setShowUpgradeModal(false);
  navigate('/authentication/sign-up?upgrade=demo');
};

// Add checklist management functions
const handleAddChecklistItem = () => {
  if (newItem.description && newItem.weight && !isNaN(newItem.weight) && newItem.weight > 0) {
    setChecklists(prev => {
      const currentChecklist = prev[0] || { name: newChecklistName || 'Default Checklist', items: [] };
      const totalWeight = currentChecklist.items.reduce((sum, item) => sum + Number(item.weight), 0) + Number(newItem.weight);
      if (totalWeight > 100) {
        setModalMessage('Total weight cannot exceed 100%');
        setModalOpen(true);
        return prev;
      }
      return [{ ...currentChecklist, items: [...currentChecklist.items, { ...newItem, weight: Number(newItem.weight) }] }];
    });
    setNewItem({ description: '', weight: '' });
  }
};

const handleRemoveChecklistItem = (index) => {
  setChecklists(prev => {
    const currentChecklist = prev[0] || { name: newChecklistName || 'Default Checklist', items: [] };
    const updatedItems = currentChecklist.items.filter((_, i) => i !== index);
    return [{ ...currentChecklist, items: updatedItems }];
  });
};


const saveChecklist = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (user) {
    try {
      // Always save the current checklist state
      if (checklists.length > 0) {
        const checklistData = {
          name: checklists[0].name || 'Default Checklist',
          items: checklists[0].items || [],
          enabled: isChecklistEnabled  // This should match the checkbox state
        };
        
        // Update the checklist document
        await setDoc(doc(db, 'Users', user.uid, 'checklists', 'primary'), checklistData);
      }
      
      // Update the user document
      await updateDoc(doc(db, 'Users', user.uid), { 
        checklistEnabled: isChecklistEnabled 
      });
      
      setModalMessage('Checklist settings saved successfully!');
      setModalOpen(true);
    } catch (error) {
      console.error("Error saving checklist:", error);
      setModalMessage('Failed to save checklist settings.');
      setModalOpen(true);
    }
  }
};



const UpgradeModal = () => {
  // Helper to get correct PayPal plan ID
  const getPayPalPlanId = (isAnnual, discountValue) => {
    const planKey = isAnnual ? "ANNUAL" : "MONTHLY";
    const basePlanId = MEMBERSHIP_PLANS[planKey].paypalPlanId;

    if (!discountValue) return basePlanId;

    const discounted = MEMBERSHIP_PLANS[planKey].discountedPlans[discountValue];
    return discounted?.paypal || basePlanId;
  };

  // Helper to calculate discounted price
  const calculatePrice = (basePrice, discountValue) => {
    if (!discountValue) return parseFloat(basePrice);
    return Number((basePrice * (100 - discountValue) / 100).toFixed(2));
  };

  return (
    <Dialog 
      open={showUpgradeModal} 
      onClose={() => setShowUpgradeModal(false)}
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.default || '#bac7db',
          minWidth: '800px',
          maxWidth: '1000px'
        }
      }}
    >
      <DialogTitle>
        <MDTypography variant="h4" align="center">
          {subscriptionInfo.plan === 'trial_plan' 
            ? 'Special Trial Upgrade Offer' 
            : 'Choose Your Premium Plan'}
        </MDTypography>
      </DialogTitle>

      <DialogContent>
        {subscriptionInfo.plan !== 'trial_plan' && (
          <DiscountCodeInput 
            onApplyCode={validateUpgradeDiscountCode}
            validating={validatingUpgradeCode}
            error={upgradeError}
            discountInfo={upgradeDiscountInfo}
          />
        )}

        <Grid container spacing={3} mt={1}>
          {/* Special $5 Trial Upgrade */}
          {subscriptionInfo.plan === 'trial_plan' && (
            <Grid item xs={12}>
              <Card sx={{ border: '2px solid #4CAF50', boxShadow: '0 8px 25px rgba(76,175,80,0.3)' }}>
                <MDBox p={4} textAlign="center">
                  <MDTypography variant="h5" color="success" fontWeight="bold" gutterBottom>
                    Special Trial Upgrade Offer
                  </MDTypography>
                  <MDTypography variant="h2" color="success" fontWeight="bold">
                    $5
                    <MDTypography component="span" variant="h5" ml={1}>first month</MDTypography>
                  </MDTypography>
                  <MDTypography variant="body1" mt={1} mb={3}>
                    Then just $19.99/month. Cancel anytime.
                  </MDTypography>

                  <PayPalButtons
                    style={{ layout: "horizontal", color: "gold", shape: "rect", label: "subscribe" }}
                    createSubscription={(data, actions) => {
                      return actions.subscription.create({
                        plan_id: MEMBERSHIP_PLANS.TRIAL_UPGRADE.paypalPlanId
                      });
                    }}
                    onApprove={async (data) => {
                      const user = auth.currentUser;
                      const now = new Date();

                      await updateDoc(doc(db, 'subscriptions', user.uid), {
                        type: 'monthly',
                        plan: 'trial_upgrade_plan',
                        status: 'active',
                        isSpecialOffer: true,
                        specialOfferType: 'trial_upgrade',
                        subscriptionID: data.subscriptionID,
                        paypalSubscriptionId: data.subscriptionID,
                        isDemo: false,
                        planDetails: {
                          cost: '5.00',
                          regularPrice: '19.99',
                          nextBillingDate: new Date(now.getTime() + 30*24*60*60*1000).toISOString(),
                        },
                        updatedAt: now.toISOString(),
                      });

                      await updateDoc(doc(db, 'Users', user.uid), {
                        plan: 'trial_upgrade_plan',
                        subscriptionID: data.subscriptionID,
                        isDemo: false,
                        isSpecialOffer: true,
                      });

                      setShowUpgradeModal(false);
                      setModalMessage('Welcome to Premium! You got the $5 first month deal!');
                      setModalOpen(true);
                      window.location.reload();
                    }}
                  />
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Normal Plans */}
          {subscriptionInfo.plan !== 'trial_plan' && (
            <>
              {/* 7-Day Free Trial */}
              {!subscriptionInfo.hasHadTrial && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', border: '2px solid #4CAF50' }}>
                    <MDBox p={4} textAlign="center">
                      <MDTypography variant="h6" fontWeight="bold">7-Day Free Trial</MDTypography>
                      <MDTypography variant="body2" color="success" mt={1} mb={2}>
                        Try all premium features free for 7 days!
                         </MDTypography>
                      <MDTypography variant="h3" color="success" fontWeight="bold">$0.00</MDTypography>
                      <MDTypography variant="body2" mt={1}>
                        Full access to all features for 7 days.
                      </MDTypography>

                      <PayPalButtons
                        style={{ layout: "horizontal" }}
                        createSubscription={(data, actions) => 
                          actions.subscription.create({ plan_id: MEMBERSHIP_PLANS.TRIAL.paypalPlanId })
                        }
                        onApprove={async (data) => {
                          const user = auth.currentUser;
                          const now = new Date();
                          const trialEnd = new Date(now.getTime() + 7*24*60*60*1000);

                          await updateDoc(doc(db, 'subscriptions', user.uid), {
                            plan: 'trial_plan',
                            status: 'active',
                            isTrial: true,
                            hasHadTrial: true,
                            subscriptionID: data.subscriptionID,
                            paypalSubscriptionId: data.subscriptionID,
                            expiresAt: trialEnd.toISOString(),
                          });

                          await updateDoc(doc(db, 'Users', user.uid), {
                            plan: 'trial_plan',
                            isTrial: true,
                            hasHadTrial: true,
                            subscriptionID: data.subscriptionID,
                            expiresAt: trialEnd.toISOString(),
                            isDemo: false,
                          });

                          setShowUpgradeModal(false);
                          setModalMessage('Your 7-day free trial has started!');
                          setModalOpen(true);
                          window.location.reload();
                        }}
                      />
                    </MDBox>
                  </Card>
                </Grid>
              )}

              {/* Monthly Plan */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <MDBox p={4}>
                    <MDTypography variant="h6" fontWeight="bold" gutterBottom>Monthly Plan</MDTypography>
                    
                    {upgradeDiscountInfo?.type === 'percentage' ? (
                      <MDTypography variant="h4" fontWeight="bold">
                        <MDTypography component="span" variant="h4" color="text" sx={{ textDecoration: 'line-through', opacity: 0.6, mr: 1 }}>
                          ${MEMBERSHIP_PLANS.MONTHLY.price}
                        </MDTypography>
                        ${calculatePrice(MEMBERSHIP_PLANS.MONTHLY.price, upgradeDiscountInfo.value)}<small>/mo</small>
                      </MDTypography>
                    ) : (
                      <MDTypography variant="h4" fontWeight="bold">
                        ${MEMBERSHIP_PLANS.MONTHLY.price}<small>/mo</small>
                      </MDTypography>
                    )}
                    
                    <MDTypography variant="body2" color="success" mt={1}>
                      Cancel anytime!
                    </MDTypography>

                    <PayPalButtons
                      style={{ layout: "horizontal" }}
                      createSubscription={(data, actions) => 
                        actions.subscription.create({ 
                          plan_id: getPayPalPlanId(false, upgradeDiscountInfo?.value) 
                        })
                      }
                      onApprove={async (data) => {
                        const user = auth.currentUser;
                        const now = new Date();
                        const cost = calculatePrice(MEMBERSHIP_PLANS.MONTHLY.price, upgradeDiscountInfo?.value);

                        await updateDoc(doc(db, 'subscriptions', user.uid), {
                          type: 'monthly',
                          plan: 'monthly_plan',
                          status: 'active',
                          subscriptionID: data.subscriptionID,
                          paypalSubscriptionId: data.subscriptionID,
                          isDemo: false,
                          planDetails: {
                            cost: cost.toString(),
                            originalCost: MEMBERSHIP_PLANS.MONTHLY.price,
                            nextBillingDate: new Date(now.getTime() + 30*24*60*60*1000).toISOString(),
                            discountApplied: upgradeDiscountInfo ? {
                              code: upgradeDiscountCode,
                              value: upgradeDiscountInfo.value,
                              type: 'percentage'
                            } : null
                          },
                          updatedAt: now.toISOString(),
                        });

                        await updateDoc(doc(db, 'Users', user.uid), {
                          plan: 'monthly_plan',
                          subscriptionID: data.subscriptionID,
                          isDemo: false,
                        });

                        setShowUpgradeModal(false);
                        setModalMessage('Monthly subscription activated!');
                        setModalOpen(true);
                        window.location.reload();
                      }}
                    />
                  </MDBox>
                </Card>
              </Grid>

              {/* Annual Plan */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', border: '2px solid #FFD700', position: 'relative' }}>
                  <MDBox position="absolute" top={8} right={8} bgcolor="gold" color="black" px={2} py={0.5} borderRadius="md">
                    <MDTypography variant="caption" fontWeight="bold">BEST VALUE</MDTypography>
                  </MDBox>
                  
                  <MDBox p={4}>
                    <MDTypography variant="h6" fontWeight="bold" gutterBottom>Annual Plan</MDTypography>
                    
                    {upgradeDiscountInfo?.type === 'percentage' ? (
                      <MDTypography variant="h4" fontWeight="bold">
                        <MDTypography component="span" variant="h4" color="text" sx={{ textDecoration: 'line-through', opacity: 0.6, mr: 1 }}>
                          ${MEMBERSHIP_PLANS.ANNUAL.price}
                        </MDTypography>
                        ${calculatePrice(MEMBERSHIP_PLANS.ANNUAL.price, upgradeDiscountInfo.value)}<small>/yr</small>
                        <MDTypography variant="caption" display="block" color="success" mt={1}>
                          (${(calculatePrice(MEMBERSHIP_PLANS.ANNUAL.price, upgradeDiscountInfo.value)/12).toFixed(2)}/mo - Save over 20%!)
                        </MDTypography>
                      </MDTypography>
                    ) : (
                      <>
                        <MDTypography variant="h4" fontWeight="bold">
                          ${MEMBERSHIP_PLANS.ANNUAL.price}<small>/yr</small>
                        </MDTypography>
                        <MDTypography variant="caption" color="success">
                          (${(MEMBERSHIP_PLANS.ANNUAL.price / 12).toFixed(2)}/mo - Save over 20% compared to monthly)
                        </MDTypography>
                      </>
                    )}

                    <PayPalButtons
                      style={{ layout: "horizontal" }}
                      createSubscription={(data, actions) => 
                        actions.subscription.create({ 
                          plan_id: getPayPalPlanId(true, upgradeDiscountInfo?.value) 
                        })
                      }
                      onApprove={async (data) => {
                        const user = auth.currentUser;
                        const now = new Date();
                        const cost = calculatePrice(MEMBERSHIP_PLANS.ANNUAL.price, upgradeDiscountInfo?.value);

                        await updateDoc(doc(db, 'subscriptions', user.uid), {
                          type: 'annual',
                          plan: 'annual_plan',
                          status: 'active',
                          subscriptionID: data.subscriptionID,
                          paypalSubscriptionId: data.subscriptionID,
                          isDemo: false,
                          planDetails: {
                            cost: cost.toString(),
                            originalCost: MEMBERSHIP_PLANS.ANNUAL.price,
                            nextBillingDate: new Date(now.getTime() + 365*24*60*60*1000).toISOString(),
                            discountApplied: upgradeDiscountInfo ? {
                              code: upgradeDiscountCode,
                              value: upgradeDiscountInfo.value,
                              type: 'percentage'
                            } : null
                          },
                          updatedAt: now.toISOString(),
                        });

                        await updateDoc(doc(db, 'Users', user.uid), {
                          plan: 'annual_plan',
                          subscriptionID: data.subscriptionID,
                          isDemo: false,
                        });

                        setShowUpgradeModal(false);
                        setModalMessage('Annual subscription activated — you just saved big!');
                        setModalOpen(true);
                        window.location.reload();
                      }}
                    />
                  </MDBox>
                </Card>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

  const saveProfileInfo = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      try {
        const userDocRef = doc(db, 'Users', user.uid);
        await setDoc(userDocRef, {
          profileInfo,
          profilePicURL,
        }, { merge: true });
        setModalMessage("Profile saved successfully!");
        setModalOpen(true);
      } catch (error) {
        console.error("Error saving profile:", error);
        setError("Failed to save profile");
      }
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <MDBox mt={5} mb={3}>
        <Grid container spacing={3}>
          {/* Profile Picture Section */}
          <Grid item xs={12} md={4}>
            <Card style={{ minHeight: '350px', backgroundColor: 'inherit', color: 'inherit' }}>
              <CardHeader title={<MDTypography variant="h6">Profile Picture</MDTypography>} />
              <CardContent>
                <MDBox display="flex" flexDirection="column" alignItems="center">
                  <input
                    accept="image/*"
                    type="file"
                    style={{ display: 'none' }}
                    id="profile-pic-upload"
                    onChange={handleProfilePicUpload}
                  />
                  <label htmlFor="profile-pic-upload">
                    <MDButton variant="outlined" color="info" size="small" component="span">
                      Choose file
                    </MDButton>
                  </label>
                  {profilePicURL && (
                    <Avatar
                      src={profilePicURL}
                      alt="Profile Picture"
                      sx={{ width: 155, height: 155, marginTop: 2 }}
                    />
                  )}
                  <MDBox mt={2}>
                    <MDButton variant="contained" color="success" onClick={saveProfileInfo}>
                      Save Entire Profile Page
                    </MDButton>
                  </MDBox>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>

          {/* Profile Information Section */}
          <Grid item xs={12} md={4}>
            <Card style={{ minHeight: '350px', backgroundColor: 'inherit', color: 'inherit' }}>
              <CardHeader title={<MDTypography variant="h6">Profile Information</MDTypography>} />
              <CardContent>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  value={profileInfo.fullName}
                  onChange={handleInputChange}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={profileInfo.email}
                  onChange={handleInputChange}
                  margin="normal"
                />

                {teamLogoUrl && profileInfo.teamId && (
                  <MDBox mt={2} display="flex" alignItems="center" gap={2}>
                    <MDBox
                      component="img"
                      src={teamLogoUrl}
                      alt="Team Logo"
                      width="3rem"
                      height="3rem"
                      borderRadius="20%"
                    />
                    <MDTypography
                      variant="caption"
                      fontWeight="regular"
                      color={theme.palette.mode === 'dark' ? 'white' : 'dark'}
                      sx={{
                        maxWidth: "100%",
                        textAlign: "left",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Team: {teamName}
                    </MDTypography>
                  </MDBox>
                )}
                {profileInfo.teamId && (
                  <MDBox mt={2}>
  <FormControlLabel
    control={
      <Switch
        checked={profileInfo.teamPermissions?.[profileInfo.teamId] || false}
        onChange={handlePermissionToggle}
        color="primary"
      />
    }
    label={
      <MDBox display="flex" flexDirection="column">
        <MDTypography variant="body2">
          Share trades with team coach
          <Tooltip
            title="Enable to allow your team coach to view your trade data for personalized guidance. Disable anytime."
            placement="top"
          >
            <InfoIcon sx={{ ml: 1, fontSize: "16px", color: iconColor, cursor: "pointer" }} />
          </Tooltip>
        </MDTypography>
        <MDTypography
          variant="caption"
          color={profileInfo.teamPermissions?.[profileInfo.teamId] ? "success" : "error"}
        >
          Currently: {profileInfo.teamPermissions?.[profileInfo.teamId] ? "On" : "Off"}
        </MDTypography>
      </MDBox>
    }
  />
</MDBox>
                )}
              </CardContent>
            </Card>
          </Grid>


<Grid item xs={12} md={6} lg={4}>
  <Card style={{ minHeight: '350px', backgroundColor: 'inherit', color: 'inherit' }}>
    <CardHeader
      title={
        <MDBox display="flex" alignItems="center">
          <MDTypography variant="h6">Subscription Details</MDTypography>
          {subscriptionInfo.isGifted && (
            <MDBox ml={1}>
              <MDTypography 
                variant="button" 
                color="success" 
                sx={{ 
                  backgroundColor: 'rgba(76,175,80,0.1)', 
                  padding: '4px 8px', 
                  borderRadius: '4px' 
                }}
              >
                {subscriptionInfo.is3MonthGift ? '3 Month Gifted Access' : 'Lifetime Access'}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      }
    />
<CardContent>
  <MDBox display="flex" flexDirection="column" gap={2}>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <MDBox>
          <MDTypography variant="button" fontWeight="medium">
            Current Plan
          </MDTypography>
          <MDTypography
            variant="body2"
            color={subscriptionInfo.isGifted ? 'success' : subscriptionInfo.isDemo ? 'warning' : 'success'}
          >
            {subscriptionInfo.isGifted ? (subscriptionInfo.is3MonthGift ? '3 Month Gifted Access' : 'Gifted Membership') : subscriptionInfo?.plan?.replace('_', ' ')}
          </MDTypography>
          {subscriptionInfo.isGifted && (
            <MDTypography variant="caption" color="success" display="block">
              {subscriptionInfo.is3MonthGift ? 'Gifted access account' : 'Special lifetime access account'}
            </MDTypography>
          )}
        </MDBox>

        {subscriptionInfo.isDemo && (
          <MDBox>
            <MDTypography
              variant="caption"
              color="warning"
              display="block"
            >
              Demo Account - Limited Features, try out our free trial for full access - upgrade below!
            </MDTypography>
            {subscriptionInfo.expiresAt && (
              <MDTypography
                variant="caption"
                color="text"
                display="block"
                mb={2}
              >
                Demo expires: {new Date(subscriptionInfo.expiresAt).toLocaleDateString()}
              </MDTypography>
            )}
            <MDButton
              variant="gradient"
              color="success"
              fullWidth
              onClick={handleUpgradeAccount}
            >
              Upgrade to Premium
            </MDButton>
          </MDBox>
        )}
        
        {subscriptionInfo?.subscriptionID && (
          <MDBox>
            <MDTypography variant="button" color="text">
            Cost
  </MDTypography>
  <MDTypography variant="body2">
    ${new Date() > new Date(subscriptionInfo.planDetails?.nextBillingDate) 
      ? subscriptionInfo.planDetails?.regularPrice 
      : subscriptionInfo.planDetails?.cost}/
    {subscriptionInfo.type === 'monthly' ? 'month' : 'year'}
  </MDTypography>
</MDBox>
        )}
      </Grid>

      {/* Second Column */}
      <Grid item xs={12} sm={6}>
        {subscriptionInfo.planDetails?.nextBillingDate && (
          <MDBox>
            <MDTypography variant="button" color="text">
              Next Billing Date
            </MDTypography>
            <MDTypography variant="body2">
              {new Date(subscriptionInfo.planDetails.nextBillingDate).toLocaleDateString()}
            </MDTypography>
          </MDBox>
        )}

        {subscriptionInfo.planDetails?.discountApplied && (
          <MDBox>
            <MDTypography variant="caption" color="success">
              Discount Applied: {subscriptionInfo.planDetails.discountApplied.value}% off
            </MDTypography>
          </MDBox>
        )}

        {subscriptionInfo?.subscriptionID && (
          <MDBox>
            <MDTypography variant="caption" color="text">
              Subscription ID: {subscriptionInfo.subscriptionID}
            </MDTypography>
          </MDBox>
        )}

        {subscriptionInfo.isGifted && (
          <MDBox>
            <MDTypography variant="caption" color="success">
              {subscriptionInfo.is3MonthGift ? '3 Month Gifted Access' : 'Lifetime Access'}
            </MDTypography>
            {subscriptionInfo.expiresAt && subscriptionInfo.is3MonthGift && (
              <MDTypography
                variant="caption"
                color="text"
                display="block"
              >
                Expires: {new Date(subscriptionInfo.expiresAt).toLocaleDateString()}
              </MDTypography>
            )}
          </MDBox>
        )}
      </Grid>
    </Grid>
    
    {/* Trial upgrade card - moved outside grid columns to span full width */}
    {subscriptionInfo.plan === 'trial_plan' && (
      <MDBox mt={2}>
        <Card sx={{ 
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: 'rgba(76,175,80,0.1)',
          border: '1px solid #4CAF50',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '@keyframes pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)' },
            '70%': { boxShadow: '0 0 0 10px rgba(76, 175, 80, 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' }
          },
          animation: 'pulse 2s infinite'
        }}>
          <MDBox p={2} textAlign="center">
            <MDTypography variant="h6" color="success" fontWeight="bold">
              Special Trial Upgrade Offer
            </MDTypography>
            <MDTypography variant="body2" mb={1}>
              Continue your premium access for just $5 for your first month!
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              Then $19.99/month. Cancel anytime.
            </MDTypography>
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleUpgradeAccount}
              sx={{ minHeight: '42px', minWidth: '220px', maxWidth: '280px' }}
            >
              Get Special Offer
            </MDButton>
          </MDBox>
        </Card>
      </MDBox>
    )}

    {/* Subscription Management */}
    {subscriptionInfo?.subscriptionID && subscriptionInfo.status !== 'cancelled' && (
      <MDBox mt={2} display="flex" justifyContent="space-between" gap={2}>


        <MDButton
          variant="gradient"
          color="info"
          fullWidth
          onClick={() => setShowChangePlanModal(true)}
        >
          Change / Cancel Plan
        </MDButton>
      </MDBox>
    )}
  </MDBox>
</CardContent>
{subscriptionInfo.status === 'cancelled' && (
  <MDBox 
    sx={{ backgroundColor: theme.palette.background.default || '#bac7db' }}
    p={2}
    borderRadius="lg"
  >
    <MDTypography variant="caption" color="warning">
      Subscription cancelled. Premium access until {new Date(subscriptionInfo.convertToDemoAt).toLocaleDateString()}
    </MDTypography>
  </MDBox>
)}
{/* Subscription Cancel Modal */}
<Dialog open={showCancelModal} onClose={() => setShowCancelModal(false)} PaperProps={{ style: { backgroundColor: theme.palette.background.default || '#bac7db' } }}>
  <DialogTitle>Cancel Subscription</DialogTitle>
  <DialogContent>
    <MDTypography variant="body2">
      Are you sure you want to cancel your subscription? You'll lose access to premium features.
    </MDTypography>
  </DialogContent>
  <DialogActions>
    <MDButton onClick={() => setShowCancelModal(false)}>
      Keep Subscription
    </MDButton>
    <MDButton 
      color="error"
      onClick={handleCancelSubscription}  // Changed this to use direct API
    >
      Cancel Subscription
    </MDButton>
  </DialogActions>
</Dialog>

{/* Change Plan Modal */}
<Dialog open={showChangePlanModal} onClose={() => setShowChangePlanModal(false)} PaperProps={{ style: { backgroundColor: theme.palette.background.default || '#bac7db' } }}>
  <DialogTitle>Manage Subscription</DialogTitle>
  <DialogContent>
    <MDBox p={2}>
      <MDTypography variant="h6" mb={2}>
        Manage Your Subscription
      </MDTypography>
      <MDTypography variant="body2" color="text" mb={2}>
        {subscriptionInfo.stripeSubscriptionId
          ? "To change or cancel your subscription plan, you'll be redirected to Stripe's customer portal."
          : "To change or cancel your subscription plan, you'll be redirected to PayPal's subscription management page."}
      </MDTypography>
      <MDButton
        variant="gradient"
        color="info"
        fullWidth
        onClick={subscriptionInfo.stripeSubscriptionId ? handleManageStripeSubscription : () => {
          window.open('https://www.paypal.com/myaccount/autopay/', '_blank');
          setShowChangePlanModal(false);
        }}
      >
        {subscriptionInfo.stripeSubscriptionId ? 'Manage Subscription in Stripe' : 'Manage Subscription in PayPal'}
      </MDButton>
    </MDBox>
  </DialogContent>
</Dialog>
      </Card>
    </Grid>
          {/* About Me Section */}
          <Grid item xs={12} md={4}>
            <Card style={{ backgroundColor: 'inherit', color: 'inherit' }}>
              <CardHeader title={<MDTypography variant="h6">About Me</MDTypography>} />
              <CardContent>
                <Editor
                  style={{ height: '200px', width: '100%', backgroundColor: 'inherit', color: 'inherit' }}
                  value={profileInfo.aboutMe}
                  onTextChange={handleAboutMeChange}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Trading Strategy Section */}
          <Grid item xs={12} md={4}>
            <Card style={{ backgroundColor: 'inherit', color: 'inherit' }}>
              <CardHeader title={<MDTypography variant="h6">Trading Strategy</MDTypography>} />
              <CardContent>
                <Editor
                  style={{ height: '200px', width: '100%', backgroundColor: 'inherit', color: 'inherit' }}
                  value={profileInfo.tradingStrategy}
                  onTextChange={handleTradingStrategyChange}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Trading Goals Section */}
          <Grid item xs={12} md={4}>
            <Card style={{ backgroundColor: 'inherit', color: 'inherit' }}>
              <CardHeader title={<MDTypography variant="h6">Trading Goals</MDTypography>} />
              <CardContent>
                <Editor
                  style={{ height: '200px', width: '100%', backgroundColor: 'inherit', color: 'inherit' }}
                  value={profileInfo.tradingGoals}
                  onTextChange={handleTradingGoalsChange}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card style={{ backgroundColor: 'inherit', color: 'inherit' }}>
              <CardHeader
                title={
                  <MDBox display="flex" alignItems="center">
                    <MDTypography variant="h6">Trade Checklist</MDTypography>
                    <Tooltip
                      title="A customisable checklist to ensure you meet your trading criteria before entering a trade. Enable it, add items with weights (up to 100% total), and save it for use in trade entries."
                      placement="top"
                    >
                      <InfoIcon sx={{ ml: 1, color: iconColor, cursor: 'pointer' }} />
                    </Tooltip>
                  </MDBox>
                }
              />
              <CardContent>
                <MDBox display="flex" flexDirection="column" height="100%">
                <FormControlLabel
                control={
                  <Checkbox 
                    checked={isChecklistEnabled} 
                    onChange={async (e) => {
                      const newState = e.target.checked;
                      setIsChecklistEnabled(newState);
                      
                      // Auto-save the enabled state when changed
                      const auth = getAuth();
                      const user = auth.currentUser;
                      if (user) {
                        try {
                          // Update the user document with the new checklistEnabled state
                          await updateDoc(doc(db, 'Users', user.uid), { 
                            checklistEnabled: newState 
                          });
                          
                          // Update the checklist document if it exists
                          if (checklists.length > 0) {
                            await updateDoc(doc(db, 'Users', user.uid, 'checklists', 'primary'), {
                              enabled: newState
                            });
                          }
                          
                          // Show success message
                          setModalMessage(newState ? 
                            'Checklist enabled successfully!' : 
                            'Checklist disabled successfully!');
                          setModalOpen(true);
                        } catch (error) {
                          console.error("Error updating checklist state:", error);
                          setModalMessage('Failed to update checklist state.');
                          setModalOpen(true);
                        }
                      }
                    }}
                  />
                }
                label="Enable Trade Checklist"
              />
                  <TextField
                    fullWidth
                    label="Checklist Name"
                    value={newChecklistName}
                    onChange={(e) => setNewChecklistName(e.target.value)}
                    margin="normal"
                    disabled={!isChecklistEnabled}
                  />
                  <MDBox display="flex" gap={1} mt={2}>
                    <TextField
                      label="Item Description"
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      disabled={!isChecklistEnabled}
                    />
                    <TextField
                      label="Weight (%)"
                      type="number"
                      value={newItem.weight}
                      onChange={(e) => setNewItem({ ...newItem, weight: e.target.value })}
                      disabled={!isChecklistEnabled}
                      sx={{ width: '100px' }}
                    />
                    <IconButton
                      onClick={handleAddChecklistItem}
                      disabled={!isChecklistEnabled || checklists[0]?.items?.length >= 10}
                      sx={{ color: iconColor }}
                    >
                      <AddIcon />
                    </IconButton>
                  </MDBox>
<MDBox mt={2}>
  {checklists[0]?.items?.map((item, index) => (
    <MDBox
      key={index}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={1}
      p={1}
      borderRadius="16px"
      bgcolor={theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}
      border={`1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`}
      sx={{
        '&:hover': {
          bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.15)'
            : 'rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      {editingIndex === index ? (
        <MDBox display="flex" gap={1} width="100%" alignItems="center">
          <TextField
            value={editingItem.description}
            onChange={(e) =>
              setEditingItem({ ...editingItem, description: e.target.value })
            }
            size="small"
            fullWidth
            disabled={!isChecklistEnabled}
          />
          <TextField
            type="number"
            value={editingItem.weight}
            onChange={(e) =>
              setEditingItem({ ...editingItem, weight: e.target.value })
            }
            size="small"
            sx={{ width: '90px' }}
            disabled={!isChecklistEnabled}
          />
          <MDButton
            size="small"
            variant="contained"
            color="success"
            onClick={handleSaveEditedItem}
            disabled={!isChecklistEnabled}
          >
            Save
          </MDButton>
        </MDBox>
      ) : (
        <>
          <MDTypography variant="body2">
            {item.description} ({item.weight}%)
          </MDTypography>

          <MDBox display="flex" gap={0.5}>
            <IconButton
              onClick={() => handleEditChecklistItem(index)}
              disabled={!isChecklistEnabled}
              sx={{ color: iconColor }}
            >
              ✏️
            </IconButton>

            <IconButton
              onClick={() => handleRemoveChecklistItem(index)}
              disabled={!isChecklistEnabled}
              sx={{ color: iconColor }}
            >
              <DeleteIcon />
            </IconButton>
          </MDBox>
        </>
      )}
    </MDBox>
  ))}
</MDBox>
                  <MDBox flexGrow={1} />
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                    <MDTypography variant="caption" color="text">
                      Total Weight: {checklists[0]?.items?.reduce((sum, item) => sum + item.weight, 0) || 0}%
                    </MDTypography>
                    <MDButton
                      variant="contained"
                      color="success"
                      onClick={saveChecklist}
                      disabled={!isChecklistEnabled || !checklists[0]?.items?.length}
                    >
                      Save Checklist
                    </MDButton>
                  </MDBox>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
              {/* Coach Set Targets Section */}
    {profileInfo.teamId && (
      <Grid item xs={12} md={4}>
        <CoachSetTargetsCard
          goals={coachGoals}
          metrics={userMetrics}
          loading={metricsLoading}
          theme={theme}
        />
      </Grid>
      
      
    )}
        {profileInfo.teamId && (
            <Grid item xs={12} md={4}>
    <CoachLogsCard
      logs={coachLogs}
      loading={coachLogsLoading}
      theme={theme}
    />
  </Grid>
      )}
        </Grid>

      </MDBox>

      <Footer />

{/* Notifications Dialog */}
<Dialog 
  open={modalOpen} 
  onClose={() => setModalOpen(false)}
  PaperProps={{ 
    style: { 
      backgroundColor: theme.palette.background.default || '#bac7db' 
    }
  }}
>
  <DialogTitle>Notification</DialogTitle>
  <DialogContent>{modalMessage}</DialogContent>
  <DialogActions>
    <MDButton onClick={() => setModalOpen(false)}>Close</MDButton>
  </DialogActions>
</Dialog>
      {/* Permission Confirmation Modal */}
      <Dialog
        open={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
        PaperProps={{ style: { backgroundColor: theme.palette.background.default || "#bac7db" } }}
      >
        <DialogTitle>Confirm Coach Permission</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">
            {pendingPermissionChange
              ? "Enabling this will allow your coach to have full visibility of your trades. This provides deep insights into your trading stats, enabling personalized coaching to improve your performance. You can turn this off anytime."
              : "Disabling this will prevent your coach from viewing your trades. You can turn this back on anytime."}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setShowPermissionModal(false)}>Cancel</MDButton>
          <MDButton
            variant="gradient"
            color={pendingPermissionChange ? "success" : "error"}
            onClick={confirmPermissionChange}
          >
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={!!error} onClose={() => setError(null)}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="error">
            {error}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setError(null)}>Close</MDButton>
        </DialogActions>
      </Dialog>


      <UpgradeModal />
    </DashboardLayout>
  );
};

export default Overview;